import { useCallback, useEffect, useState } from 'react';
import { cancellableFetch } from '$shared/utils';
import { MYPAGE_API_URL } from '$shared/utils/search/service/endpoints';
import { useInfiniteQuery } from '@tanstack/react-query';
import { OrdersResponseViewModel } from '~/lib/data-contract/auto-generated/api-mypage-service-model';

const PAGE_SIZE = 24;

export const useOrders = (searchText?: string, supplementaryAgreements = false) => {
    const [page, setPage] = useState<number>(0);

    useEffect(() => {
        setPage(0);
    }, [searchText]);

    const {
        data,
        isFetching,
        fetchNextPage,
        isLoading,
    } = useInfiniteQuery<OrdersResponseViewModel>(
        ['orders', supplementaryAgreements, searchText],
        ({ signal, pageParam = { pageNumber: 0 } }) =>
            cancellableFetch({
                signal,
                url: `${MYPAGE_API_URL}/Order/${
                    supplementaryAgreements ? 'GetSupplementaryAgreements' : 'GetOrders'
                }?from=${pageParam.pageNumber * PAGE_SIZE}&take=${PAGE_SIZE}${
                    searchText ? `&searchText=${searchText?.toLowerCase()}` : ''
                }`,
            }),
        {
            enabled: true,
            keepPreviousData: true,
        }
    );

    const orderData = data?.pages.reduce((a, c) => ({
        ...c,
        orders: [...(a.orders || []), ...(c.orders || [])],
    }));

    const loadMore = useCallback(() => {
        if ((orderData?.total || 0) > (orderData?.orders?.length || 0)) {
            fetchNextPage({ pageParam: { pageNumber: page + 1 } });
            setPage(page + 1);
        }
    }, [fetchNextPage, page, orderData]);

    return {
        hasNextPage: (orderData?.total || 0) > (orderData?.orders?.length || 0),
        orderData,
        isFetching,
        loadMore,
        isLoading,
    };
};
